<template>
  <header class="common-header">
    <div class="app-title-container">
      <img class="app-logo" src="/favicon.png" alt="Logo" />
      <h1 class="app-title">{{ t('app.title') }}</h1>
    </div>
    <div class="user-section">
      <el-dropdown trigger="click" :disabled="isLoggingOut">
        <span class="el-dropdown-link">
          <el-avatar :size="36" class="user-avatar">
            {{ avatarText }}
          </el-avatar>
        </span>
        <template #dropdown>
          <el-dropdown-menu>
            <el-dropdown-item disabled>
              <div class="user-info">
                <div class="username">{{ usernameText }}</div>
                <div class="roles">{{ rolesText }}</div>
              </div>
            </el-dropdown-item>
            <el-dropdown-item divided @click="openChangePasswordDialog">修改密码</el-dropdown-item>
            <el-dropdown-item :disabled="isLoggingOut" @click="handleLogout">
              {{ isLoggingOut ? '正在退出…' : '退出登录' }}
            </el-dropdown-item>
          </el-dropdown-menu>
        </template>
      </el-dropdown>
    </div>

    <!-- 修改密码弹窗 -->
    <el-dialog
      v-model="changePasswordVisible"
      title="修改密码"
      width="420px"
      :close-on-click-modal="false"
      @closed="resetPasswordForm"
    >
      <el-form
        ref="passwordFormRef"
        :model="passwordForm"
        :rules="passwordRules"
        label-width="100px"
        label-position="right"
      >
        <el-form-item label="旧密码" prop="oldPassword">
          <el-input
            v-model="passwordForm.oldPassword"
            type="password"
            placeholder="请输入旧密码"
            show-password
          />
        </el-form-item>
        <el-form-item label="新密码" prop="newPassword">
          <el-input
            v-model="passwordForm.newPassword"
            type="password"
            placeholder="请输入新密码"
            show-password
          />
        </el-form-item>
        <el-form-item label="确认密码" prop="confirmPassword">
          <el-input
            v-model="passwordForm.confirmPassword"
            type="password"
            placeholder="请再次输入新密码"
            show-password
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="changePasswordVisible = false">取消</el-button>
        <el-button type="primary" :loading="isChangingPassword" @click="handleChangePassword">
          确定
        </el-button>
      </template>
    </el-dialog>
  </header>
</template>

<script lang="ts">
import { changePassword } from '@/services/authService';
import { useUserStore } from '@/stores/user';
import {
  ElLoading,
  ElMessage,
  ElMessageBox,
  type FormInstance,
  type FormRules,
} from 'element-plus';
import { computed, defineComponent, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

export default defineComponent({
  name: 'CommonHeader',
  setup() {
    const { t } = useI18n();
    const router = useRouter();
    const userStore = useUserStore();

    const isLoggingOut = ref(false);

    const usernameText = computed(() => userStore.username || '未登录');
    const rolesText = computed(() =>
      userStore.roleName ? `角色：${userStore.roleName}` : '角色：未分配',
    );
    const avatarText = computed(() =>
      userStore.username ? userStore.username[0]?.toUpperCase() : '?',
    );

    async function handleLogout() {
      if (isLoggingOut.value) return; // 防止重复点击
      isLoggingOut.value = true;
      const loading = ElLoading.service({
        lock: true,
        text: '正在退出…',
        background: 'rgba(0, 0, 0, 0.2)',
      });
      try {
        await userStore.logout();
        router.push('/login');
      } finally {
        // 跳转后组件可能卸载，但这里仍保证状态回收
        loading.close();
        isLoggingOut.value = false;
      }
    }

    // 修改密码相关
    const changePasswordVisible = ref(false);
    const isChangingPassword = ref(false);
    const passwordFormRef = ref<FormInstance>();
    const passwordForm = reactive({
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    });

    const validateConfirmPassword = (
      _rule: unknown,
      value: string,
      callback: (error?: Error) => void,
    ) => {
      if (value !== passwordForm.newPassword) {
        callback(new Error('两次输入的密码不一致'));
      } else {
        callback();
      }
    };

    const passwordRules: FormRules = {
      oldPassword: [{ required: true, message: '请输入旧密码', trigger: 'blur' }],
      newPassword: [
        { required: true, message: '请输入新密码', trigger: 'blur' },
        { min: 6, message: '密码长度不能少于6位', trigger: 'blur' },
      ],
      confirmPassword: [
        { required: true, message: '请确认新密码', trigger: 'blur' },
        { validator: validateConfirmPassword, trigger: 'blur' },
      ],
    };

    function openChangePasswordDialog() {
      changePasswordVisible.value = true;
    }

    function resetPasswordForm() {
      passwordForm.oldPassword = '';
      passwordForm.newPassword = '';
      passwordForm.confirmPassword = '';
      passwordFormRef.value?.clearValidate();
    }

    async function handleChangePassword() {
      if (!passwordFormRef.value) return;
      const valid = await passwordFormRef.value.validate().catch(() => false);
      if (!valid) return;

      isChangingPassword.value = true;
      try {
        const resp = await changePassword(passwordForm.oldPassword, passwordForm.newPassword);
        if (resp.success) {
          changePasswordVisible.value = false;
          // 密码修改成功后显示确认弹窗再退出
          await ElMessageBox.alert('密码修改成功，请重新登录', '提示', {
            confirmButtonText: '确定',
            type: 'success',
            showClose: false,
            closeOnClickModal: false,
            closeOnPressEscape: false,
          });
          await userStore.logout();
          router.push('/login');
        } else {
          ElMessage.error(resp.message || '密码修改失败');
        }
      } catch {
        ElMessage.error('密码修改失败，请稍后重试');
      } finally {
        isChangingPassword.value = false;
      }
    }

    return {
      t,
      usernameText,
      rolesText,
      avatarText,
      handleLogout,
      isLoggingOut,
      // 修改密码
      changePasswordVisible,
      isChangingPassword,
      passwordFormRef,
      passwordForm,
      passwordRules,
      openChangePasswordDialog,
      resetPasswordForm,
      handleChangePassword,
    };
  },
});
</script>

<style scoped>
.common-header {
  background-color: #f5f5f5;
  height: var(--nav-height);
  padding: 0 20px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.app-title-container {
  display: flex;
  align-items: center;
}

.app-logo {
  width: 40px;
  height: 40px;
  margin-right: 6px;
  margin-bottom: 4px;
  display: inline-block;
}

.app-title {
  font-size: 24px;
  font-weight: bold;
  display: inline-block;
}

.user-section {
  display: flex;
  align-items: center;
}
.user-avatar {
  cursor: pointer;
}
.user-info {
  display: flex;
  flex-direction: column;
}
.username {
  font-weight: 600;
}
.roles {
  font-size: 12px;
  color: #666;
}
</style>
